/**
 * Ordering-block banner (Sheila directive, 2026-08-25): shown on the family +
 * weekly-orders pages while the household is blocked from ordering — by an
 * unresolved bank dispute, or by support acting manually (derived from
 * `/api/family/me`'s `disputeBlock`). Ordering is already off at that point and
 * the household's auto-orders are PAUSED, so this banner exists to explain WHY
 * before the parent hits a 402 at checkout.
 *
 * Deliberately CTA-less to Billing: unlike a failed payment, nothing the parent
 * can do in Ordo lifts this. The cure runs through support, so the only action
 * offered is the mail link. The copy is the server's message, passed through
 * verbatim, so there is one source for it — and the server is what decides
 * whether it names a bank dispute (a MANUAL block must not).
 */

import { Button } from '@ordo/ui/button'

import type { MyFamilyAccount } from '@/api/family'
import { supportMailto } from '@/lib/support'

/** Manual blocks must not put "Bank dispute" in the parent's outgoing subject. */
function mailSubject(source: NonNullable<MyFamilyAccount['disputeBlock']>['source']): string {
  return source === 'MANUAL' ? 'Ordering is disabled on my Ordo account' : 'Bank dispute on my Ordo account'
}

export function DisputeBlockBanner({
  disputeBlock,
}: {
  disputeBlock: NonNullable<MyFamilyAccount['disputeBlock']> | null
}) {
  if (!disputeBlock) {
    return null
  }
  return (
    <div
      data-testid="dispute-block-banner"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-300/60 bg-red-50 px-4 py-3 dark:border-red-500/40 dark:bg-red-950/30">
      <p className="text-sm font-medium text-foreground">{disputeBlock.message}</p>
      <Button variant="default" size="sm" asChild>
        <a href={supportMailto(mailSubject(disputeBlock.source))}>Contact support</a>
      </Button>
    </div>
  )
}
